import React, { createContext, useCallback, useMemo, useState } from 'react';

import { UserInvite, DistrolistInvite, CommunityInvite } from 'types/invites.types';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';

export type InviteGuestsModalStep = 'SELECT' | 'SETTINGS';

export interface InviteGuestsModalState {
  selectedUsers: UserInvite[];
  selectedDistrolists: DistrolistInvite[];
  selectedCommunities: CommunityInvite[];
  /** True when the email importer skipped people who already have a status on the event. */
  emailDedupeRequired: boolean;
  currentStep: InviteGuestsModalStep;
  calendarEmailAvailable: boolean;
  sendCalendarEmail: boolean;
  sendSlackInvite: boolean;
}

export interface InviteGuestsModalActions {
  toggleUser: (user: UserInvite) => void;
  toggleDistrolist: (distrolist: DistrolistInvite) => void;
  toggleCommunity: (community: CommunityInvite) => void;
  addEmailImports: (users: UserInvite[]) => void;
  markEmailDedupeRequired: () => void;
  clearAll: () => void;
  setStep: (step: InviteGuestsModalStep) => void;
  setSendCalendarEmail: (value: boolean) => void;
  setSendSlackInvite: (value: boolean) => void;
  reset: () => void;
}

export interface InviteGuestsModalContextValue
  extends InviteGuestsModalState,
    InviteGuestsModalActions {}

const InviteGuestsModalContext = createContext<InviteGuestsModalContextValue | undefined>(
  undefined
);

interface InviteGuestsModalProviderProps {
  children: React.ReactNode;
}

export const InviteGuestsModalProvider: React.FC<InviteGuestsModalProviderProps> = ({
  children
}) => {
  const event = useEditableEvent();
  const calendarEmailAvailable = !!(event.calendarNotifications && event.sendCalendarInvites);

  const [selectedUsers, setSelectedUsers] = useState<UserInvite[]>([]);
  const [selectedDistrolists, setSelectedDistrolists] = useState<DistrolistInvite[]>([]);
  const [selectedCommunities, setSelectedCommunities] = useState<CommunityInvite[]>([]);
  const [emailDedupeRequired, setEmailDedupeRequired] = useState(false);
  const [currentStep, setCurrentStep] = useState<InviteGuestsModalStep>('SELECT');
  // Only the admin's opt-out, never availability. Availability is applied when deriving
  // sendCalendarEmail below, so the checkbox stays in sync when calendar settings change
  // while the editor (and this provider) stay mounted.
  const [calendarEmailChoice, setCalendarEmailChoice] = useState(true);
  const [sendSlackInvite, setSendSlackInvite] = useState(true);

  const sendCalendarEmail = calendarEmailAvailable && calendarEmailChoice;

  const toggleUser = useCallback((user: UserInvite) => {
    setSelectedUsers((prev) => {
      // Check if user is already selected by ID or email
      const isSelectedById = user.id ? prev.find((u) => u.id === user.id) : undefined;
      const isSelectedByEmail = prev.find((u) => u.email === user.email);

      if (isSelectedById || isSelectedByEmail) {
        // Remove user
        return prev.filter((u) => {
          const idMatches = user.id && u.id === user.id;
          const emailMatches = u.email === user.email;
          return !idMatches && !emailMatches;
        });
      }

      // Add user
      return [...prev, user];
    });
  }, []);

  const toggleDistrolist = useCallback((distrolist: DistrolistInvite) => {
    setSelectedDistrolists((prev) => {
      const isSelected = prev.find((d) => d.id === distrolist.id);

      if (isSelected) {
        // Remove distrolist
        return prev.filter((d) => d.id !== distrolist.id);
      }

      // Add distrolist
      return [...prev, distrolist];
    });
  }, []);

  const toggleCommunity = useCallback((community: CommunityInvite) => {
    setSelectedCommunities((prev) => {
      if (prev.find((c) => c.id === community.id)) {
        return prev.filter((c) => c.id !== community.id);
      }

      return [...prev, community];
    });
  }, []);

  const addEmailImports = useCallback((users: UserInvite[]) => {
    setSelectedUsers((prev) => {
      // Filter out duplicates based on email
      const filteredUsers = users.filter(
        (newUser) => !prev.find((existingUser) => existingUser.email === newUser.email)
      );
      return [...prev, ...filteredUsers];
    });
  }, []);

  const markEmailDedupeRequired = useCallback(() => {
    setEmailDedupeRequired(true);
  }, []);

  const clearAll = useCallback(() => {
    setSelectedUsers([]);
    setSelectedDistrolists([]);
    setSelectedCommunities([]);
    setEmailDedupeRequired(false);
  }, []);

  const setStep = useCallback((step: InviteGuestsModalStep) => {
    setCurrentStep(step);
  }, []);

  // Wrapped rather than handing out the raw setter, which would also accept an updater
  // whose `prev` is the stored choice — not the derived sendCalendarEmail callers see.
  const setSendCalendarEmail = useCallback((value: boolean) => {
    setCalendarEmailChoice(value);
  }, []);

  const reset = useCallback(() => {
    setSelectedUsers([]);
    setSelectedDistrolists([]);
    setSelectedCommunities([]);
    setEmailDedupeRequired(false);
    setCurrentStep('SELECT');
    setCalendarEmailChoice(true);
    setSendSlackInvite(true);
  }, []);

  const value = useMemo<InviteGuestsModalContextValue>(
    () => ({
      // State
      selectedUsers,
      selectedDistrolists,
      selectedCommunities,
      emailDedupeRequired,
      currentStep,
      calendarEmailAvailable,
      sendCalendarEmail,
      sendSlackInvite,
      // Actions
      toggleUser,
      toggleDistrolist,
      toggleCommunity,
      addEmailImports,
      markEmailDedupeRequired,
      clearAll,
      setStep,
      setSendCalendarEmail,
      setSendSlackInvite,
      reset
    }),
    [
      selectedUsers,
      selectedDistrolists,
      selectedCommunities,
      emailDedupeRequired,
      currentStep,
      calendarEmailAvailable,
      sendCalendarEmail,
      sendSlackInvite,
      toggleUser,
      toggleDistrolist,
      toggleCommunity,
      addEmailImports,
      markEmailDedupeRequired,
      clearAll,
      setStep,
      reset
    ]
  );

  return (
    <InviteGuestsModalContext.Provider value={value}>{children}</InviteGuestsModalContext.Provider>
  );
};

export { InviteGuestsModalContext };
